import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AIStepType, useAILoadingContext } from '../AIFormStepContext'
import { usePromptGenerator } from '../PromptGeneratorContext'
import { useChatDictation } from 'components/ui/ChatComposer/useChatDictation'
import { usePromptReferenceImages } from './usePromptReferenceImages'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import useLocaleText from 'components/useLocaleText'
import { useAiCreationText } from './useAiCreationText'
import { mediaUploadLiterals } from 'components/TasksV2/TaskCreation/components/MediaUploadActions'
export interface IAIPromptStepData {
  prompt: string
  reference_images: string[]
}

export interface IAIPromptStepProps {
  showContinue?: boolean
  /**
   * Overrides what happens on "Next". Default (AI task creation): persist the prompt and move to
   * AI_SETTINGS. The VM sub-flow uses it to start generation and jump straight to AI_SUBTASK.
   */
  onNext?: (data: IAIPromptStepData) => void
  onReadyChange?: (ready: boolean) => void
  onSaveDraft?: (data: IAIPromptStepData) => void
  /**
   * Overrides how the prompt-helper conversation is opened. Default: push `AI_PROMPT_GENERATOR` onto the
   * step stack (works wherever the stack drives what is rendered). The full-page flow renders the steps
   * inside its own subtask modal, so it hands its local navigation in here instead.
   */
  onOpenPromptGenerator?: () => void
}

export function useAiPromptForm({ onNext, onOpenPromptGenerator, onSaveDraft, onReadyChange }: IAIPromptStepProps) {
  const [hasPromptError, setHasPromptError] = useState(false)

  const { setCurrentFormSubmit, updateFormData, formData, setIsMediaUploading, setStepStack } = useFormStepContext()
  const { selectedSubtaskType } = useAILoadingContext()
  const isQuestion = selectedSubtaskType === 'question'
  const imageInputRef = useRef<HTMLInputElement>(null)
  const { isAvailable: isPromptGeneratorAvailable } = usePromptGenerator()

  const t_prompt_title = useLocaleText('ai_prompt_title')
  const photoSubtitle = useLocaleText('ai_prompt_msg')
  const t = useAiCreationText()
  const t_prompt_subtitle = isQuestion ? t.questionPromptHint : photoSubtitle
  const t_prompt_vm = useLocaleText('step1_vm_placeholder')
  const t_prompt_qst = t.questionPromptPlaceholder

  const t_prompt_generator = useLocaleText('prompt_generator')
  const uploadText = useLocaleText(mediaUploadLiterals)

  const savedPromptData = (formData as any)['AI_PROMPT']

  const { handleSubmit, control, watch, setValue, getValues } = useForm({
    defaultValues: {
      prompt: savedPromptData?.prompt || '',
      reference_images: savedPromptData?.reference_images || ([] as string[]),
    },
  })

  const referenceImages: string[] = watch('reference_images') || []
  const promptValue: string = watch('prompt') || ''

  const draftRef = useRef({ prompt: promptValue, reference_images: referenceImages })
  draftRef.current = { prompt: promptValue, reference_images: referenceImages }
  const lastSavedDraft = useRef('')
  const saveDraftRef = useRef(onSaveDraft)
  saveDraftRef.current = onSaveDraft

  const input = useRef<HTMLTextAreaElement>(null)
  const dictation = useChatDictation({
    text: promptValue,
    onChange: (value) => {
      setValue('prompt', value)
      setHasPromptError(false)
    },
    active: true,
    input,
  })
  const { cleanup, listening } = dictation.speech
  const { cancel: cancelDictation } = dictation

  const { isUploading, uploadingUrls, uploadImages } = usePromptReferenceImages({
    readImages: () => (getValues('reference_images') as string[]) || [],
    writeImages: (images) => setValue('reference_images', images),
    onBusyChange: setIsMediaUploading,
  })

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith('image/'))
    event.target.value = ''
    await uploadImages(files)
  }

  const handleRemoveMedia = (index: number) => {
    setValue(
      'reference_images',
      referenceImages.filter((_, i) => i !== index)
    )
  }

  const handleDropMedia = async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'))
    if (!imageFiles.length) return
    await uploadImages(imageFiles)
  }

  const handleReorderMedia = (startIndex: number, endIndex: number) => {
    const reordered = [...referenceImages]
    const [moved] = reordered.splice(startIndex, 1)
    reordered.splice(endIndex, 0, moved)
    setValue('reference_images', reordered)
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleDropMedia,
    accept: 'image/*',
  })

  // The helper conversation is a step of this same modal. Persist what is typed so far — this step
  // unmounts while the helper is open, and its result comes back through the AI_PROMPT slice.
  const handleOpenPromptGenerator = () => {
    if (isUploading) return

    updateFormData('AI_PROMPT' as any, { ...(formData as any)?.['AI_PROMPT'], ...getValues() })
    if (onOpenPromptGenerator) {
      onOpenPromptGenerator()
      return
    }
    setStepStack((prev) => [...prev, AIStepType.AI_PROMPT_GENERATOR])
  }

  // Keep the latest parent callback without replacing the registered submit on every render.
  const onNextRef = useRef(onNext)
  onNextRef.current = onNext

  useEffect(() => {
    if (!listening) return
    const cancelRecording = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      cancelDictation()
    }
    document.addEventListener('keydown', cancelRecording, true)
    return () => document.removeEventListener('keydown', cancelRecording, true)
  }, [listening, cancelDictation])

  useEffect(() => {
    onReadyChange?.(!!promptValue.trim() && !isUploading && !dictation.speech.listening)
  }, [promptValue, isUploading, dictation.speech.listening, onReadyChange])

  useEffect(() => {
    const serialized = JSON.stringify(draftRef.current)
    if (serialized !== lastSavedDraft.current) {
      lastSavedDraft.current = serialized
      saveDraftRef.current?.(draftRef.current)
    }
  })

  useEffect(
    () => () => {
      if (saveDraftRef.current) saveDraftRef.current(draftRef.current)
      else updateFormData('AI_PROMPT' as any, draftRef.current)
    },
    [updateFormData]
  )

  useEffect(() => {
    setCurrentFormSubmit(() =>
      handleSubmit((data) => {
        if (dictation.speech.listening || isUploading) return
        cleanup()
        if (!data.prompt?.trim()) {
          setHasPromptError(true)
          return
        }
        setHasPromptError(false)
        if (onNextRef.current) {
          onNextRef.current(data as IAIPromptStepData)
          return
        }
        updateFormData('AI_PROMPT' as any, { ...(formData as any)?.['AI_PROMPT'], ...data })
        setStepStack((prev) => [...prev, AIStepType.AI_SETTINGS])
      })
    )
  }, [dictation.speech.listening, isUploading]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    control,
    promptValue,
    setValue,
    hasPromptError,
    setHasPromptError,
    input,
    dictation,
    isQuestion,
    isUploading,
    uploadingUrls,
    referenceImages,
    imageInputRef,
    handleImageUpload,
    uploadImages,
    handleRemoveMedia,
    handleReorderMedia,
    onDrop,
    handlePaste,
    handleOpenPromptGenerator,
    isPromptGeneratorAvailable,
    t_prompt_title,
    t_prompt_subtitle,
    t_prompt_vm,
    t_prompt_qst,
    t_prompt_generator,
    uploadText,
  }
}
